import {
  AvailabilityType,
  DateChoicesEnum,
  KindOfTimeType,
  OrderLimitType,
  ProductOverrideType,
  WorkingDayType,
} from '@/constants/enum';
import {
  IPickupDisable,
  IPickupOverrideDateRanges,
  IPickupOverrideHolidays,
  IPickupOverrideSpecificWorkingDays,
  IPickupSettings,
  IPickupState,
} from '@/types/components/pickup';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { IHoliday, ISpecificWorkingDay, IWeeklyWorkingDay } from '@/types/components/day';

const pickupSettingInitial: IPickupSettings = {
  id: -1,
  shop: '',
  locationId: -1,
  isEnable: false,
  isDefault: false,
  pickupChoice: DateChoicesEnum.DATETIMESLOT,
  workingDayType: WorkingDayType.WEEKDAYS,
  pickupTimeSlotType: KindOfTimeType.SAME,
  pickupPreparationTimeType: KindOfTimeType.SAME,
  orderLimitType: OrderLimitType.LIMIT_PER_DAY,
  overrideType: ProductOverrideType.OVERRIDE_SPECIFIC_WORKING_DAYS,
  pickupDateRange: { value: '14', unit: 'day' }, // ex: { value: 1, unit: 'day' }
  productAvailabilityType: AvailabilityType.ALL,
  isEnablePreparationTime: false,
  additionalTags: [], // ex: [] tag1, tag2, tag3 ]
  updatedAt: '',
  createdAt: '',
};

const pickupStateInitial: IPickupState = {
  isSaving: false,
  isLoading: false,
  isCheckingTimeSlot: false,
};

const pickupDisableInitial: IPickupDisable = {
  isDisableSpecificProduct: false,
  isDisableProductOverride: false,
  isDisableOrderLimitPerTimeSlot: false,
};

const pickupWeeklyWorkingDaysInitial: IWeeklyWorkingDay[] = [];

const pickupSpecificWorkingDaysInitial: ISpecificWorkingDay[] = [];

const pickupHolidayInitial: IHoliday[] = [];

const pickupProductsInitial: string[] = [];

const pickupCollectionsInitial: string[] = [];

const pickupOverrideSpecificWorkingDaysInitial: IPickupOverrideSpecificWorkingDays[] = [];

const pickupOverrideHolidayInitial: IPickupOverrideHolidays[] = [];

const pickupOverrideDateRangesInitial: IPickupOverrideDateRanges[] = [];

const initialState = {
  pickup: {
    pickupSettings: pickupSettingInitial,
    pickupState: pickupStateInitial,
    pickupWeeklyWorkingDays: pickupWeeklyWorkingDaysInitial,
    pickupWeeklyWorkingDaysLastState: pickupWeeklyWorkingDaysInitial,
    pickupSpecificWorkingDays: pickupSpecificWorkingDaysInitial,
    pickupHolidays: pickupHolidayInitial,
    pickupProducts: pickupProductsInitial,
    pickupCollections: pickupCollectionsInitial,
    pickupOverrideSpecificWorkingDays: pickupOverrideSpecificWorkingDaysInitial,
    pickupOverrideHolidays: pickupOverrideHolidayInitial,
    pickupOverrideDateRanges: pickupOverrideDateRangesInitial,
  },
  pickupBackup: {
    pickupSettings: pickupSettingInitial,
    pickupState: pickupStateInitial,
    pickupWeeklyWorkingDays: pickupWeeklyWorkingDaysInitial,
    pickupWeeklyWorkingDaysLastState: pickupWeeklyWorkingDaysInitial,
    pickupSpecificWorkingDays: pickupSpecificWorkingDaysInitial,
    pickupHolidays: pickupHolidayInitial,
    pickupProducts: pickupProductsInitial,
    pickupCollections: pickupCollectionsInitial,
    pickupOverrideSpecificWorkingDays: pickupOverrideSpecificWorkingDaysInitial,
    pickupOverrideHolidays: pickupOverrideHolidayInitial,
    pickupOverrideDateRanges: pickupOverrideDateRangesInitial,
  },
  pickupDisable: pickupDisableInitial,
};

const pickupSlice = createSlice({
  name: 'pickup',
  initialState,
  reducers: {
    handleSavePickupSettings: (state, action: PayloadAction<Partial<IPickupSettings>>) => {
      state.pickup.pickupSettings = { ...state.pickup.pickupSettings, ...action.payload };
    },
    handleSetPickupState: (state, action: PayloadAction<Partial<IPickupState>>) => {
      state.pickup.pickupState = { ...state.pickup.pickupState, ...action.payload };
    },
    handleSavePickupWeeklyWorkingDays: (state, action: PayloadAction<IWeeklyWorkingDay[]>) => {
      state.pickup.pickupWeeklyWorkingDays = action.payload;
    },
    handleSavePickupWeeklyWorkingDaysLastState: (state, action: PayloadAction<IWeeklyWorkingDay[]>) => {
      state.pickup.pickupWeeklyWorkingDaysLastState = action.payload;
    },
    handleSavePickupSpecificWorkingDays: (state, action: PayloadAction<ISpecificWorkingDay[]>) => {
      state.pickup.pickupSpecificWorkingDays = action.payload;
    },
    handleSavePickupHolidays: (state, action: PayloadAction<IHoliday[]>) => {
      state.pickup.pickupHolidays = action.payload;
    },
    handleSavePickupProducts: (state, action: PayloadAction<string[]>) => {
      state.pickup.pickupProducts = action.payload;
    },
    handleSavePickupCollections: (state, action: PayloadAction<string[]>) => {
      state.pickup.pickupCollections = action.payload;
    },
    handleSaveOverridePickupSpecificWorkingDays: (state, action: PayloadAction<IPickupOverrideSpecificWorkingDays[]>) => {
      state.pickup.pickupOverrideSpecificWorkingDays = action.payload;
    },
    handleSaveOverridePickupHolidays: (state, action: PayloadAction<IPickupOverrideHolidays[]>) => {
      state.pickup.pickupOverrideHolidays = action.payload;
    },
    handleSaveOverridePickupDateRanges: (state, action: PayloadAction<IPickupOverrideDateRanges[]>) => {
      state.pickup.pickupOverrideDateRanges = action.payload;
    },
    handleSaveBackupPickupSettings: (state, action: PayloadAction<Partial<IPickupSettings>>) => {
      state.pickupBackup.pickupSettings = { ...state.pickupBackup.pickupSettings, ...action.payload };
    },
    handleSetBackupPickupState: (state, action: PayloadAction<Partial<IPickupState>>) => {
      state.pickupBackup.pickupState = { ...state.pickupBackup.pickupState, ...action.payload };
    },
    handleSaveBackupPickupWeeklyWorkingDays: (state, action: PayloadAction<IWeeklyWorkingDay[]>) => {
      state.pickupBackup.pickupWeeklyWorkingDays = action.payload;
    },
    handleSaveBackupPickupWeeklyWorkingDaysLastState: (state, action: PayloadAction<IWeeklyWorkingDay[]>) => {
      state.pickupBackup.pickupWeeklyWorkingDaysLastState = action.payload;
    },
    handleSaveBackupPickupSpecificWorkingDays: (state, action: PayloadAction<ISpecificWorkingDay[]>) => {
      state.pickupBackup.pickupSpecificWorkingDays = action.payload;
    },
    handleSaveBackupPickupHolidays: (state, action: PayloadAction<IHoliday[]>) => {
      state.pickupBackup.pickupHolidays = action.payload;
    },
    handleSaveBackupPickupProducts: (state, action: PayloadAction<string[]>) => {
      state.pickupBackup.pickupProducts = action.payload;
    },
    handleSaveBackupPickupCollections: (state, action: PayloadAction<string[]>) => {
      state.pickupBackup.pickupCollections = action.payload;
    },
    handleSaveBackupOverridePickupSpecificWorkingDays: (state, action: PayloadAction<IPickupOverrideSpecificWorkingDays[]>) => {
      state.pickupBackup.pickupOverrideSpecificWorkingDays = action.payload;
    },
    handleSaveBackupOverridePickupHolidays: (state, action: PayloadAction<IPickupOverrideHolidays[]>) => {
      state.pickupBackup.pickupOverrideHolidays = action.payload;
    },
    handleSaveBackupOverridePickupDateRanges: (state, action: PayloadAction<IPickupOverrideDateRanges[]>) => {
      state.pickupBackup.pickupOverrideDateRanges = action.payload;
    },
    handleResetPickupInitial: (state) => {
      state.pickup = initialState.pickup;
      state.pickupBackup = initialState.pickupBackup;
    },
    handleResetPickupState: (state) => {
      state.pickup.pickupState = pickupStateInitial;
    },
    handleResetPickup: (state) => {
      state.pickup = state.pickupBackup;
    },
    handleSetPickupDisable: (state, action: PayloadAction<Partial<IPickupDisable>>) => {
      state.pickupDisable = { ...state.pickupDisable, ...action.payload };
    },
  },
});

export const pickupSettingSelector = createSelector(
  (state: RootState) => state.pickup,
  (state) => state.pickup.pickupSettings,
);
export const pickupStateSelector = createSelector(
  (state: RootState) => state.pickup,
  (state) => state.pickup.pickupState,
);
export const pickupWeeklyWorkingDaySelector = createSelector(
  (state: RootState) => state.pickup,
  (state) => state.pickup.pickupWeeklyWorkingDays,
);
export const pickupWeeklyWorkingDaysLastStateSelector = createSelector(
  (state: RootState) => state.pickup,
  (state) => state.pickup.pickupWeeklyWorkingDaysLastState,
);
export const pickupSpecificWorkingDaySelector = createSelector(
  (state: RootState) => state.pickup,
  (state) => state.pickup.pickupSpecificWorkingDays,
);
export const pickupHolidaySelector = createSelector(
  (state: RootState) => state.pickup,
  (state) => state.pickup.pickupHolidays,
);
export const pickupProductSelector = createSelector(
  (state: RootState) => state.pickup,
  (state) => state.pickup.pickupProducts,
);
export const pickupCollectionSelector = createSelector(
  (state: RootState) => state.pickup,
  (state) => state.pickup.pickupCollections,
);
export const pickupOverrideSpecificWorkingDaysSelector = createSelector(
  (state: RootState) => state.pickup,
  (state) => state.pickup.pickupOverrideSpecificWorkingDays,
);
export const pickupOverrideHolidaysSelector = createSelector(
  (state: RootState) => state.pickup,
  (state) => state.pickup.pickupOverrideHolidays,
);
export const pickupOverrideDateRangesSelector = createSelector(
  (state: RootState) => state.pickup,
  (state) => state.pickup.pickupOverrideDateRanges,
);
export const currentPickupSelector = createSelector(
  (state: RootState) => state.pickup,
  (pickup) => pickup.pickup,
);
export const oldPickupSelector = createSelector(
  (state: RootState) => state.pickup,
  (state) => state.pickupBackup,
);
export const pickupDisableSelector = createSelector(
  (state: RootState) => state.pickup.pickupDisable,
  (pickupDisable) => pickupDisable,
);

export const {
  handleSavePickupSettings,
  handleSetPickupState,
  handleSavePickupWeeklyWorkingDays,
  handleSavePickupWeeklyWorkingDaysLastState,
  handleSavePickupSpecificWorkingDays,
  handleSavePickupHolidays,
  handleSavePickupProducts,
  handleSavePickupCollections,
  handleSaveOverridePickupSpecificWorkingDays,
  handleSaveOverridePickupHolidays,
  handleSaveOverridePickupDateRanges,
  handleSaveBackupPickupSettings,
  handleSetBackupPickupState,
  handleSaveBackupPickupWeeklyWorkingDays,
  handleSaveBackupPickupWeeklyWorkingDaysLastState,
  handleSaveBackupPickupSpecificWorkingDays,
  handleSaveBackupPickupHolidays,
  handleSaveBackupPickupProducts,
  handleSaveBackupPickupCollections,
  handleSaveBackupOverridePickupSpecificWorkingDays,
  handleSaveBackupOverridePickupHolidays,
  handleSaveBackupOverridePickupDateRanges,
  handleResetPickup,
  handleResetPickupState,
  handleResetPickupInitial,
  handleSetPickupDisable,
} = pickupSlice.actions;

export default pickupSlice;
